<template>
    <Rows-container 
        ref="rowsContainer" 
        class="details-lines"
        :items-props="partLines"
        @on-down="onDownRows"
    >
        <template v-for="(item, index) in partLines" :key="item.lineId">
            <line-view-skeleton v-if="item.cardItems === null" :name="item.skeleton" />
            <row-view
                v-if="checkExist(item)"
                :ref="item.ref"
                :index-row="index"
                :key-data="item.keyData"
                :item="item"
                :items="item.cardItems"
                :line-id="item.lineId"
                :title="item.title"
                @loaded-line="onLoadedLine"
                @list-item-selected="itemsSelected"
            />
        </template>
    </Rows-container>
</template>

<script lang="ts" setup>
    import { ref, type Ref, computed, type ComputedRef, watch, nextTick } from "vue"
    import { useI18n } from "vue-i18n";
    import { Strings } from "@/locales/types/enums";
    import { AppConstants } from "@/common";
    import type { RowsContainerRef } from "@/components/RowsContainer/types";
    import RowsContainer from "@/components/RowsContainer/RowsContainer.vue";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import { useCastPageStore } from "@/stores/CastPage";
    import type { RowConfig, IEventSelected } from "@/components/RowView/types";
    import { storeToRefs } from 'pinia';
    import LineViewSkeleton from "@/components/Skeleton/LineViewSkeleton.vue";
    import { useParentControlStore } from "@/stores/ParentControl";
    import type { ExposeInterface } from "./types";
    import { ComponentName as SkeletonName } from "@/components/Skeleton/types";


    const ParentControlStoreModule = useParentControlStore();
    const {
        ShowParentModal,
        SetTargetWidgetAdultControl,
        SetIncorrectAdultPin,
    } = ParentControlStoreModule;
    const {
        getIsAdultPinCorrect: isAdultPinCorrect,
        getTargetWidgetAdultControl: targetWidgetAdultControl,
    } = storeToRefs(ParentControlStoreModule);

    const CastPageStore = useCastPageStore();
    const {
        getCounterLinesDetailMovie,
        getCardsCastRelated,
        getCardsMedia
    } = storeToRefs(CastPageStore);
    

    const { increaseCounterLinesCastDetail } = CastPageStore;

    const emit = defineEmits(['show-all', 'list-item-selected', 'ready', 'focus-to-catcher']);
    const { t } = useI18n();
    const selectedItem: Ref<IEventSelected | null> = ref(null);

    const rowsContainer: Ref<RowsContainerRef | null> = ref(null); 
    const { setCurrentTimes, checkParentControlCard } = useParentControl();
    const name = "LinesDetailMovie";
    const { shelfKeys }: typeof AppConstants = AppConstants;

    const allLines: ComputedRef<RowConfig[]> = computed(() => {
        return [
            ...getCardsMedia.value,
            {
                keyData: shelfKeys.castCards,
                title: t(Strings.CAST_PAGE_RELATED_TITLE).toString(),
                ref: shelfKeys.castCards,
                lineId: shelfKeys.castCards,
                skeleton: SkeletonName.MOVIE_CARD_SKELETON,
                cardItems: getCardsCastRelated.value,
            }
        ];
    });
    const partLines: ComputedRef<RowConfig[]> = computed(() => allLines.value.slice(0, getCounterLinesDetailMovie.value));

    const checkExist = (item: RowConfig): boolean => {
        const isExist = item.cardItems !== null && item.cardItems.length > 0;
        if (!isExist) {
            onLoadedLine();
        } 
        return isExist;
    };
    
    const counter = ref(1);
    const onLoadedLine = (): void => {
        if (rowsContainer.value === null) {
            return;
        }
        const countLoadedRows = rowsContainer.value.getCountLoadedRows();
        if (countLoadedRows !== null && getCounterLinesDetailMovie.value >= countLoadedRows) {
            void nextTick(() => {
                emit('ready');
            });
        }

        if (counter.value <= AppConstants.HomeCounterLines) {
            const isIncreasing = (counter.value % AppConstants.HomeCounterLines) != 0; 
            if (isIncreasing) {
                increaseCounterLinesCastDetail();
                counter.value++;
            }
        }
    };

    const onListItemSelected = (): void => {
        emit("list-item-selected", selectedItem.value);
    };
    const itemsSelected = (event: IEventSelected): void => {
        selectedItem.value = event;

        checkParentControlCard(event).then((isAllow)=>{
            if (isAllow) {
                onListItemSelected();
            } else {
                ShowParentModal();
                SetTargetWidgetAdultControl(name);
            }
        }).catch((error)=> {
            Logger.captureError(error)
        });
    };

    const onDownRows = (): void => {
        if (getCounterLinesDetailMovie.value < allLines.value.length) {
            increaseCounterLinesCastDetail();
        }
    };

    const focusFirstRow = (): void => {
        if (rowsContainer.value) {
            rowsContainer.value.focusFirstRow();
        }
    };

    watch(
        isAdultPinCorrect, 
        (value: boolean): void => {
            if (value && name === targetWidgetAdultControl.value) {
                SetIncorrectAdultPin();
                setCurrentTimes();
                onListItemSelected();
            }
        }
    );

    defineExpose<ExposeInterface>({
        focusFirstRow
    });
</script>